/**
 * The ranking window, as a state a screen can render.
 *
 * The same shape as the overview's hook, for the same reason: a page that had
 * to work out what a combination of booleans means would eventually work it out
 * differently from the next page.
 *
 * Reading this never makes the strategy look at the market. The endpoint serves
 * the last published window and nothing else, so refreshing a browser costs the
 * exchange nothing — which is why the refetch below can be routine.
 */
import { useQuery } from "@tanstack/react-query";

import { useSession } from "@/features/auth";
import type { RankingResponse } from "@/shared/api";
import { rankingKey, readCurrentRanking } from "@/shared/ranking/readCurrentRanking";
import { statusOf } from "@/shared/http/errors";

export type RankingState =
  | { readonly status: "loading" }
  | { readonly status: "forbidden" }
  | { readonly status: "unreadable" }
  | { readonly status: "ready"; readonly ranking: RankingResponse };

export function useRanking(): RankingState {
  const platform = useSession().status === "platform";
  const query = useQuery({
    queryKey: rankingKey(platform),
    queryFn: () => readCurrentRanking(platform),
    // Slower than the overview on purpose. The window changes only when a slot
    // falls free, so asking every fifteen seconds would mostly re-fetch the
    // same rows; a minute keeps the countdown honest without the noise.
    refetchInterval: 60_000,
    retry: false,
  });

  // Offline refresh cannot confirm that a cached FRESH verdict still holds.
  if (query.fetchStatus === "paused") return { status: "unreadable" };
  if (query.isPending) return { status: "loading" };
  if (query.isError) {
    return statusOf(query.error) === 403
      ? { status: "forbidden" }
      : { status: "unreadable" };
  }
  return { status: "ready", ranking: query.data };
}
